import React, { useState, useEffect } from 'react';
import { withRouter } from "react-router-dom";

import { store } from "react-notifications-component";

import api from '../../../services/api';
import estados from '../../../services/estados';

import HeaderV2 from '../../../components/headerV2';
import Input from '../../../components/input/input_v2';
import ButtonV2 from '../../../components/button/button_v2';
import ButtonFilter from '../../../components/button-filter/indexV2';
import PaginatorV2 from "../../../components/paginator/indexV2";
import TableWithLabelBlackV2 from '../../../components/TableWithLabelBlack/indexV2';

import * as S from './styles';
import { theme } from '../../../styles/theme';
import { TaglineP } from '../../../styles/stylesTypography';

const estadosAbreviados = estados.map(estado => ({
    value: estado.value,
    label: estado.value,
}));

const LIMIT = 30;

const NotasFiscais = ({ ...props }) => {
    const [width, setWidth] = useState(window.innerWidth);
    const [dataInicio, setDataInicio] = useState("");
    const [dataFim, setDataFim] = useState("");
    const [ufFilter, setUfFilter] = useState(null);
    const [receitas, setReceitas] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [total, setTotal] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);

    const [selectedOptions, setSelectedOptions] = useState({ uf: [] });
    const [openSelect, setOpenSelect] = useState("");

    useEffect(() => {
        fetchReceitas();
    }, []); 

    const toggleSelect = (key) => {
        setOpenSelect(prev => prev === key ? "" : key);
    };

    // Chamado quando o usuário confirma a seleção pendente no ButtonFilterV2
    // (botão APLICAR) - substitui o antigo addSelectOption (que aplicava a
    // cada clique numa opção). A busca de verdade continua só disparando no
    // clique do botão "Filtrar", como já era.
    const handleApplyFilter = (key, values) => {
        setSelectedOptions(prev => {
            const newSelected = { ...prev, [key]: values };
            setUfFilter(values.length === 1 ? values[0] : null);
            return newSelected;
        });
    };

    const fetchReceitas = async (limit = LIMIT, offset = 0) => {
        setIsLoading(true);
        try {
            const payload = {
                data_inicio: dataInicio,
                data_fim: dataFim,
                estado: ufFilter || "",
            };

            const response = await api.post(`/relatorios/nf?limit=${limit}&offset=${offset}`, payload);

            if (response.status === 200) {
                setReceitas(Array.isArray(response.data) ? response.data : response.data?.data || []);
                setTotal(response.data?.total || 0);
                setCurrentPage(Math.floor(offset / limit) + 1);
            }
        } catch (error) {
            store.addNotification({
                title: "Erro",
                message: "Não foi possível buscar as notas fiscais.",
                type: "danger",
                insert: "top",
                container: "top-right",
                animationIn: ["animate__animated", "animate__fadeIn"],
                animationOut: ["animate__animated", "animate__fadeOut"],
                dismiss: { duration: 4000 },
            });
        } finally {
            setIsLoading(false);
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return "-";
        const date = new Date(dateString);
        const day = date.getUTCDate().toString().padStart(2, "0");
        const month = (date.getUTCMonth() + 1).toString().padStart(2, "0");
        const year = date.getUTCFullYear();
        return `${day}/${month}/${year}`;
    };

    const columns = [
        {
            label: "Nº NF",
            key: "nf",
            minWidth: "100px",
            render: (row) => (
                <TaglineP color={theme.colors.gray.Cinza_1}>
                    {row.nf || "-"}
                </TaglineP>
            ),
        },
        {
            label: "Chave de Acesso",
            key: "chave_acesso",
            width: "100%",
            minWidth: "200px",
            maxWidth: "350px",
            enableHorizontalScroll: true,
            render: (row) => (
                <TaglineP color={theme.colors.gray.Cinza_1}>
                    {row.chave_acesso || "-"}
                </TaglineP>
            ),
        },
        {
            label: "Série",
            key: "serie",
            minWidth: "80px",
            render: (row) => (
                <TaglineP color={theme.colors.gray.Cinza_1}>
                    {row.serie || "-"}
                </TaglineP>
            ),
        },
        {
            label: "Cliente",
            key: "razao_social_cliente",
            width: "100%",
            minWidth: "220px",
            maxWidth: "350px",
            enableHorizontalScroll: true,
            render: (row) => (
                <TaglineP color={theme.colors.gray.Cinza_1}>
                    {row.razao_social_cliente || "-"}
                </TaglineP>
            ),
        },
        {
            label: "Estado",
            key: "estado",
            minWidth: "90px",
            render: (row) => (
                <TaglineP color={theme.colors.gray.Cinza_1}>
                    {row.estado || "-"}
                </TaglineP>
            ),
        },
        {
            label: "Produtos",
            key: "produtos",
            width: "100%",
            minWidth: "220px",
            maxWidth: "400px",
            enableHorizontalScroll: true,
            render: (row) => {
                const items = row.agro_prescription_invoice_items || [];
                return (
                    <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                        {items.length > 0 ? (
                            items.map((item, idx) => (
                                <TaglineP key={idx} color={theme.colors.gray.Cinza_1}>
                                    {`${item.nome_produto || "-"} — ${item.qtd_produto || ""} ${item.unidade_medida || ""}`.trim()}
                                </TaglineP>
                            ))
                        ) : (
                            <TaglineP color={theme.colors.gray.Cinza_1}>-</TaglineP>
                        )}
                    </div>
                );
            },
        },
        {
            label: "Data Emissão",
            key: "data_emissao",
            minWidth: "140px",
            width: "100%",
            maxWidth: "200px",
            render: (row) => (
                <TaglineP color={theme.colors.gray.Cinza_1}>
                    {formatDate(row.data_emissao)}
                </TaglineP>
            ),
        },
    ];

    useEffect(() => {
        const handleResize = () => {
            setWidth(window.innerWidth);
        };

        window.addEventListener('resize', handleResize);
        handleResize();

        return () => window.removeEventListener('resize', handleResize);
    }, []);

    return (
        <S.Container>
            <HeaderV2 title="Notas Fiscais" />

            <S.Wrapper>
                <S.ContainerFilters>
                    <Input
                        id="dataInicio"
                        type="date"
                        name="dataInicio"
                        label="Data Início"
                        value={dataInicio}
                        onChange={(value) => setDataInicio(value)}
                        required={false}
                        minHeight="auto"
                        marginBottom="0"
                    />

                    <Input
                        id="dataFim"
                        type="date"
                        name="dataFim"
                        label="Data Fim"
                        value={dataFim}
                        onChange={(value) => setDataFim(value)}
                        required={false}
                        minHeight="auto"
                        marginBottom="0"
                    />

                    <ButtonFilter
                        key_value="uf"
                        selectedOptions={selectedOptions}
                        label={width > 760 ? "Estado" : "UF"}
                        options={{ uf: estadosAbreviados }}
                        toggleSelect={toggleSelect}
                        onApply={handleApplyFilter}
                        openSelect={openSelect}
                        justIconFilter={false}
                        borderColor={theme.colors.gray.Cinza_7}
                        showClearFilter={false}
                        maxHeightOptionsList="10rem"
                    />

                    <ButtonV2
                        id="filtrar"
                        name="filtrar"
                        value="Filtrar"
                        width="120px"
                        height="40px"
                        onClick={() => fetchReceitas(LIMIT, 0)}
                        loading={isLoading}
                    />
                </S.ContainerFilters>

                <TableWithLabelBlackV2
                    columns={columns}
                    data={receitas}
                    showAll={true}
                    itemsPerPage={100}
                    serverSide={true}
                    enableColumnFilters={false}
                    useServerSideColumnFilters={true}
                    isLoading={isLoading}
                    emptyMessage="Não há resultados"
                    onColumnFiltersChange={() => {}}
                    onCountChange={() => {}}
                />

                <PaginatorV2
                    perPage={LIMIT}
                    total={total}
                    onChange={(limit, offset) => fetchReceitas(limit, offset)}
                    currentPage={currentPage}
                    totalItens={total}
                />
            </S.Wrapper>
        </S.Container>
    );
};

export default withRouter(NotasFiscais);
